/* eslint-disable import/no-extraneous-dependencies -- required here */
import * as React from 'react';
import { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { mergeRegister, $getNearestNodeOfType, $findMatchingParent } from '@lexical/utils';
import { $isHeadingNode } from '@lexical/rich-text';
import { $getSelection, $isRangeSelection, $isRootOrShadowRoot, FORMAT_TEXT_COMMAND, FORMAT_ELEMENT_COMMAND, UNDO_COMMAND, REDO_COMMAND, SELECTION_CHANGE_COMMAND, COMMAND_PRIORITY_CRITICAL, CAN_UNDO_COMMAND, CAN_REDO_COMMAND, DEPRECATED_$isGridSelection } from 'lexical';
import { $isListNode, ListNode } from '@lexical/list';
import { $getSelectionStyleValueForProperty, $isAtNodeEnd, $patchStyleText } from '@lexical/selection';
import { $isLinkNode, TOGGLE_LINK_COMMAND } from '@lexical/link';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $isTableNode } from '@lexical/table';
import styleNames from '@pareto-engineering/bem/exports';
import { BlockFormatDropDown } from "../BlockFormatDropDown";
import { ColorPicker } from "../ColorPicker";
import { FontSizeDropDown } from "../FontSizeDropDown";

// Local Definitions
import "./styles.scss";
const getSelectedNode = selection => {
  const {
    anchor,
    focus
  } = selection;
  const anchorNode = selection.anchor.getNode();
  const focusNode = selection.focus.getNode();
  if (anchorNode === focusNode) {
    return anchorNode;
  }
  const isBackward = selection.isBackward();
  if (isBackward) {
    return $isAtNodeEnd(focus) ? anchorNode : focusNode;
  }
  return $isAtNodeEnd(anchor) ? focusNode : anchorNode;
};
export const blockTypeToBlockName = {
  bullet: 'Bulleted List',
  check: 'Check List',
  code: 'Code Block',
  h1: 'Heading 1',
  h2: 'Heading 2',
  h3: 'Heading 3',
  number: 'Numbered List',
  paragraph: 'Normal',
  quote: 'Quote'
};
const baseClassName = styleNames.base;
const componentClassName = 'toolbar-plugin';

/**
 * This is the component description
 */
const ToolbarPlugin = ({
  id,
  className: userClassName,
  style: inlineStyles,
  baseBgColor
  // ...otherProps
}) => {
  const [editor] = useLexicalComposerContext();
  const [activeEditor, setActiveEditor] = useState(editor);
  const [blockType, setBlockType] = useState('paragraph');
  const [rootType, setRootType] = useState('root');
  const [fontSize, setFontSize] = useState('15px');
  const [isEditable, setIsEditable] = useState(() => editor.isEditable());
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isStrikethrough, setIsStrikethrough] = useState(false);
  const [isLink, setIsLink] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [canUndo, setCanUndo] = useState(false);
  const formatLink = useCallback(() => {
    if (!isLink) {
      // eslint-disable-next-line no-alert
      const path = prompt('Enter the full URL. Ex: https://www.example.com');
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, path);
    } else {
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
    }
  }, [editor, isLink]);
  const $updateToolbar = useCallback(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      const anchorNode = selection.anchor.getNode();
      let element = anchorNode.getKey() === 'root' ? anchorNode : $findMatchingParent(anchorNode, e => {
        const parent = e.getParent();
        return parent !== null && $isRootOrShadowRoot(parent);
      });
      if (element === null) {
        element = anchorNode.getTopLevelElementOrThrow();
      }
      const elementKey = element.getKey();
      const elementDOM = activeEditor.getElementByKey(elementKey);

      // Update text format
      setIsBold(selection.hasFormat('bold'));
      setIsItalic(selection.hasFormat('italic'));
      setIsUnderline(selection.hasFormat('underline'));
      setIsStrikethrough(selection.hasFormat('strikethrough'));

      // Update links
      const node = getSelectedNode(selection);
      const parent = node.getParent();
      if ($isLinkNode(parent) || $isLinkNode(node)) {
        setIsLink(true);
      } else {
        setIsLink(false);
      }
      const tableNode = $findMatchingParent(node, $isTableNode);
      if ($isTableNode(tableNode)) {
        setRootType('table');
      } else {
        setRootType('root');
      }
      if (elementDOM !== null) {
        if ($isListNode(element)) {
          const parentList = $getNearestNodeOfType(anchorNode, ListNode);
          const type = parentList ? parentList.getListType() : element.getListType();
          setBlockType(type);
        } else {
          const type = $isHeadingNode(element) ? element.getTag() : element.getType();
          if (type in blockTypeToBlockName) {
            setBlockType(type);
          }
        }
      }
      setFontSize($getSelectionStyleValueForProperty(selection, 'font-size', '15px'));
    }
  }, [activeEditor]);
  useEffect(() => editor.registerCommand(SELECTION_CHANGE_COMMAND, (_payload, newEditor) => {
    $updateToolbar();
    setActiveEditor(newEditor);
    return false;
  }, COMMAND_PRIORITY_CRITICAL), [editor, $updateToolbar]);
  useEffect(() => mergeRegister(editor.registerEditableListener(editable => {
    setIsEditable(editable);
  }), activeEditor.registerUpdateListener(({
    editorState
  }) => {
    editorState.read(() => {
      $updateToolbar();
    });
  }), activeEditor.registerCommand(CAN_UNDO_COMMAND, payload => {
    setCanUndo(payload);
    return false;
  }, COMMAND_PRIORITY_CRITICAL), activeEditor.registerCommand(CAN_REDO_COMMAND, payload => {
    setCanRedo(payload);
    return false;
  }, COMMAND_PRIORITY_CRITICAL)), [$updateToolbar, activeEditor, editor]);
  const applyStyleText = useCallback(styles => {
    activeEditor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection) || DEPRECATED_$isGridSelection(selection)) {
        $patchStyleText(selection, styles);
      }
    });
  }, [activeEditor]);
  const onFontColorSelect = useCallback(value => {
    applyStyleText({
      color: value
    });
  }, [applyStyleText]);
  const onBgColorSelect = useCallback(value => {
    applyStyleText({
      'background-color': value
    });
  }, [applyStyleText]);
  return /*#__PURE__*/React.createElement("div", {
    id: id,
    className: [baseClassName, componentClassName, userClassName, `x-${baseBgColor}`].filter(e => e).join(' '),
    style: inlineStyles
  }, /*#__PURE__*/React.createElement("div", {
    className: "group"
  }, /*#__PURE__*/React.createElement("button", {
    type: "button",
    onClick: () => editor.dispatchCommand(UNDO_COMMAND),
    className: "ai-icon",
    disabled: !canUndo || !isEditable
  }, "A"), /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "ai-icon",
    onClick: () => editor.dispatchCommand(REDO_COMMAND),
    disabled: !canRedo || !isEditable
  }, "B")), /*#__PURE__*/React.createElement(BlockFormatDropDown, {
    blockType: blockType,
    editor: editor,
    rootType: rootType
  }), /*#__PURE__*/React.createElement("div", {
    className: "group"
  }, /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: isBold ? 'active' : undefined,
    onClick: () => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold')
  }, /*#__PURE__*/React.createElement("span", {
    className: "icon"
  }, "|")), /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: isItalic ? 'active' : undefined,
    onClick: () => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic')
  }, /*#__PURE__*/React.createElement("span", {
    className: "icon"
  }, "}")), /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: isUnderline ? 'active' : undefined,
    onClick: () => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'underline')
  }, /*#__PURE__*/React.createElement("span", {
    className: "icon"
  }, "~")), /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: isStrikethrough ? 'active' : undefined,
    onClick: () => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'strikethrough')
  }, /*#__PURE__*/React.createElement("span", {
    className: "icon"
  }, "?")), /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: isLink ? 'active' : undefined,
    onClick: formatLink
  }, /*#__PURE__*/React.createElement("span", {
    className: "icon"
  }, "]"))), /*#__PURE__*/React.createElement(FontSizeDropDown, {
    editor: editor,
    value: fontSize
  }), /*#__PURE__*/React.createElement("div", {
    className: "group"
  }, /*#__PURE__*/React.createElement(ColorPicker, {
    icon: "'",
    onChange: onFontColorSelect
  }), /*#__PURE__*/React.createElement(ColorPicker, {
    icon: "#",
    onChange: onBgColorSelect
  })), /*#__PURE__*/React.createElement("div", {
    className: "group"
  }, /*#__PURE__*/React.createElement("button", {
    type: "button",
    onClick: () => editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'left')
  }, /*#__PURE__*/React.createElement("span", {
    className: "icon"
  }, "^")), /*#__PURE__*/React.createElement("button", {
    type: "button",
    onClick: () => editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'center')
  }, /*#__PURE__*/React.createElement("span", {
    className: "icon"
  }, "_")), /*#__PURE__*/React.createElement("button", {
    type: "button",
    onClick: () => editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'right')
  }, /*#__PURE__*/React.createElement("span", {
    className: "icon"
  }, "`")), /*#__PURE__*/React.createElement("button", {
    type: "button",
    onClick: () => editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'justify')
  }, /*#__PURE__*/React.createElement("span", {
    className: "icon"
  }, "{"))));
};
ToolbarPlugin.propTypes = {
  /**
   * The HTML id for this element
   */
  id: PropTypes.string,
  /**
   * The HTML class names for this element
   */
  className: PropTypes.string,
  /**
   * The React-written, css properties for this element.
   */
  style: PropTypes.objectOf(PropTypes.string),
  /**
   * Base background color
   */
  baseBgColor: PropTypes.string
};
ToolbarPlugin.defaultProps = {
  baseBgColor: 'background-far'
};
export default ToolbarPlugin;